import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import MatchSplitPane from "../components/MatchSplitPane";
import TabSearchFilter, { EMPTY_TAB_FILTER, rowPassesTabFilter } from "../components/TabSearchFilter";
import { SortPicker } from "../components/SortState";
import {
  ArrowLeft, Download, CheckCircle, XCircle, AlertTriangle,
  ChevronLeft, ChevronRight, FileText, Flag, X, Link2, Unlink,
  RotateCw, Search, Sparkles, Columns3, Check, Save, BarChart3, ShieldCheck,
} from "lucide-react";

const TABS = [
  { id: "matched", label: "Matched", badge: "badge-matched" },
  { id: "partial", label: "Partial / Review", badge: "badge-partial" },
  { id: "unmatched_a", label: "Unmatched (File A)", badge: "badge-unmatched" },
  { id: "unmatched_b", label: "Unmatched (File B)", badge: "badge-warning" },
  { id: "manual", label: "Manually Reconciled", badge: "badge-manual" },
  { id: "ignored", label: "Ignored / Non-contributing", badge: "badge-manual" },
];

const IGNORE_REASONS = [
  { value: "non_contributing", label: "Non-contributing entry" },
  { value: "outside_period", label: "Outside this period" },
  { value: "reversed_later", label: "Reversed in a later period" },
  { value: "already_resolved", label: "Already resolved elsewhere" },
  { value: "bank_fee", label: "Bank fee / charge not in ledger" },
  { value: "other", label: "Other (see remarks)" },
];

const MANUAL_REASONS = [
  { value: "timing_diff", label: "Timing difference" },
  { value: "fx_rounding", label: "FX / rounding difference" },
  { value: "already_resolved", label: "Already resolved" },
  { value: "data_entry_error", label: "Data entry error" },
  { value: "missing_ref", label: "Missing reference / ID rewrite" },
  { value: "partial_payment", label: "Partial payment / split" },
  { value: "other", label: "Other (see remarks)" },
];

const REASON_LABEL = Object.fromEntries(MANUAL_REASONS.map(r => [r.value, r.label]));

const PAGE_SIZE = 50;

// Merge single-side manual rows into one display row per manual_match_id.
// - Docs with BOTH row_a+row_b (partial-approved / conflict-resolved) pass through as-is.
// - 1:1 split docs → merged into one row.
// - 1:N or N:1 → emit one row per secondary.
function mergeManualPairs(docs) {
  const out = [];
  const groups = {};
  for (const d of docs) {
    if (d.match_type !== "manual" || !d.manual_match_id) { out.push(d); continue; }
    // Self-contained doc: has both sides already
    if (d.row_a && d.row_b) { out.push({ ...d, rule_name: d.rule_name || "Manual" }); continue; }
    const g = groups[d.manual_match_id] || { a: [], b: [], meta: d };
    if (d.row_a) g.a.push(d); else g.b.push(d);
    groups[d.manual_match_id] = g;
  }
  for (const [mm, g] of Object.entries(groups)) {
    const aList = g.a, bList = g.b;
    if (aList.length <= 1 && bList.length <= 1) {
      const a = aList[0], b = bList[0];
      out.push({
        id: `merged_${mm}`, match_type: "manual", rule_name: "Manual",
        manual_match_id: mm,
        row_a: a?.row_a || null, row_b: b?.row_b || null,
        row_a_transformed: a?.row_a_transformed, row_b_transformed: b?.row_b_transformed,
      });
    } else if (aList.length === 1) {
      for (const b of bList) {
        out.push({
          id: `merged_${mm}_${b.id}`, match_type: "manual", rule_name: "Manual",
          manual_match_id: mm,
          row_a: aList[0].row_a, row_b: b.row_b,
          row_a_transformed: aList[0].row_a_transformed, row_b_transformed: b.row_b_transformed,
        });
      }
    } else {
      for (const a of aList) {
        out.push({
          id: `merged_${mm}_${a.id}`, match_type: "manual", rule_name: "Manual",
          manual_match_id: mm,
          row_a: a.row_a, row_b: bList[0]?.row_b || null,
          row_a_transformed: a.row_a_transformed, row_b_transformed: bList[0]?.row_b_transformed,
        });
      }
    }
  }
  return out;
}

function SummaryCard({ label, value, sub, color, icon: Icon, testid }) {
  return (
    <div className="recon-card" data-testid={testid} style={{ padding: "1.25rem" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <p style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", fontWeight: 600, margin: "0 0 0.25rem" }}>{label}</p>
          <p style={{ fontSize: "1.75rem", fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, color, margin: 0 }}>{value}</p>
          {sub && <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: "0.25rem 0 0" }}>{sub}</p>}
        </div>
        {Icon && (
          <div style={{ width: "2.5rem", height: "2.5rem", borderRadius: "0.5rem", background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon size={18} style={{ color }} strokeWidth={1.5} />
          </div>
        )}
      </div>
    </div>
  );
}

// Cell that shows a value + (if transformed) an inline ↻ icon with tooltip of transformed value
function TransformCell({ original, transformed }) {
  const showArrow = transformed !== undefined && transformed !== null && String(transformed) !== String(original ?? "");
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
      <span>{original ?? "—"}</span>
      {showArrow && (
        <span
          title={`Transformed → ${transformed}`}
          data-testid="transformed-indicator"
          style={{
            display: "inline-flex", alignItems: "center", gap: "0.2rem",
            fontSize: "0.6rem", color: "#5B21B6", background: "#EDE9FE",
            border: "1px solid #DDD6FE", borderRadius: "9999px",
            padding: "0.05rem 0.4rem", fontFamily: "JetBrains Mono, monospace",
          }}>
          <RotateCw size={9} strokeWidth={2} /> {String(transformed).slice(0, 20)}
        </span>
      )}
    </span>
  );
}

export default function ResultsPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Drill-down from Rule Impact bar: /results/:id?tab=matched&rule=<name>
  const urlTab = searchParams.get("tab");
  const urlRule = searchParams.get("rule") || "";
  const [run, setRun] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const allowed = new Set(["matched", "partial", "unmatched_a", "unmatched_b", "manual", "ignored"]);
    return allowed.has(urlTab) ? urlTab : "matched";
  });
  // Server-side rule_name filter (drives the backend query + a removable chip)
  const [ruleFilter, setRuleFilter] = useState(urlRule);
  const clearRuleFilter = () => {
    setRuleFilter("");
    // Keep tab in URL, strip rule= param
    const next = new URLSearchParams(searchParams);
    next.delete("rule");
    setSearchParams(next, { replace: true });
  };
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [expandedRow, setExpandedRow] = useState(null);

  // Flagging
  const [flagRow, setFlagRow] = useState(null);
  const [flagNotes, setFlagNotes] = useState("");
  const [flagStatus, setFlagStatus] = useState("review");
  const [flagAssignee, setFlagAssignee] = useState("");
  const [flaggedIds, setFlaggedIds] = useState(new Set());
  const [deptUsers, setDeptUsers] = useState([]);

  // Manual match mode
  const [matchMode, setMatchMode] = useState(false);
  const [selectedA, setSelectedA] = useState(new Set());
  const [selectedB, setSelectedB] = useState(new Set());
  const [manualReason, setManualReason] = useState("timing_diff");
  const [manualRemarks, setManualRemarks] = useState("");
  const [splitPaneA, setSplitPaneA] = useState([]);
  const [splitPaneB, setSplitPaneB] = useState([]);
  const [submittingMatch, setSubmittingMatch] = useState(false);
  const [matchError, setMatchError] = useState("");

  // Server-side search for the manual-match split panes (300ms debounce)
  const [splitSearchA, setSplitSearchA] = useState("");
  const [splitSearchB, setSplitSearchB] = useState("");
  const [debouncedSplitA, setDebouncedSplitA] = useState("");
  const [debouncedSplitB, setDebouncedSplitB] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebouncedSplitA(splitSearchA.trim()), 300); return () => clearTimeout(t); }, [splitSearchA]);
  useEffect(() => { const t = setTimeout(() => setDebouncedSplitB(splitSearchB.trim()), 300); return () => clearTimeout(t); }, [splitSearchB]);

  // Tab search filter (reset on tab change)
  const [tabFilter, setTabFilter] = useState(EMPTY_TAB_FILTER);
  // Debounced free-text `q` (300ms) — sent server-side so search covers all pages
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ((tabFilter.text || "").trim()), 300);
    return () => clearTimeout(t);
  }, [tabFilter.text]);

  // Multi-level sort (server-side, page-agnostic)
  const [sortSpec, setSortSpec] = useState([]);
  useEffect(() => { setSortSpec([]); }, [activeTab]); // reset on tab change

  // Manually reconciled tab data
  const [manualGroups, setManualGroups] = useState([]);
  const [manualPage, setManualPage] = useState(0);
  const [rulePerf, setRulePerf] = useState(null);
  const MANUAL_PAGE_SIZE = 10;

  // Column selector (B-new) — persisted per-run
  const COL_STORAGE_KEY = `reconx_columns_${runId}`;
  const [colPrefs, setColPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COL_STORAGE_KEY) || "null") || {}; }
    catch { return {}; }
  });
  const [colsOpen, setColsOpen] = useState(false);

  // All available columns derived from run (first row per side for the *active* dataset)
  const [availA, setAvailA] = useState([]);
  const [availB, setAvailB] = useState([]);

  // Partial approve/reject (#2)
  const [selectedPartial, setSelectedPartial] = useState(new Set());
  const [partialDecisionOpen, setPartialDecisionOpen] = useState(null); // "approve" | "reject" | null
  const [partialRemarks, setPartialRemarks] = useState("");

  // E7: Ignore / non-contributing selection on unmatched tabs
  const [selectedIgnore, setSelectedIgnore] = useState(new Set());
  const [ignoreModalOpen, setIgnoreModalOpen] = useState(false);
  const [ignoreReason, setIgnoreReason] = useState("non_contributing");
  const [ignoreRemarks, setIgnoreRemarks] = useState("");
  const [ignoring, setIgnoring] = useState(false);

  // Server-side column prefs (#4)
  const [teamPrefs, setTeamPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(colPrefs)); } catch {}
  }, [COL_STORAGE_KEY, colPrefs]);

  // -----------------------------------------------------------------------
  // Data loading
  // -----------------------------------------------------------------------
  useEffect(() => {
    api.get(`/reconciliation/runs/${runId}`)
      .then(r => {
        setRun(r.data);
        // Load server-side team display prefs for this rule set (#4)
        if (r.data?.rule_set_id) {
          api.get(`/rule-sets/${r.data.rule_set_id}/display-prefs`)
            .then(p => setTeamPrefs(p.data || {}))
            .catch(() => setTeamPrefs({}));
        }
      })
      .catch(() => navigate(-1));
    api.get(`/reconciliation/runs/${runId}/rule-performance`)
      .then(r => setRulePerf(r.data))
      .catch(() => {});
  }, [runId, navigate]);

  useEffect(() => {
    api.get("/exceptions/", { params: { run_id: runId } })
      .then(r => setFlaggedIds(new Set((r.data || []).map(e => e.result_id).filter(Boolean))))
      .catch(() => {});
    api.get("/admin/dept/users")
      .catch(() => api.get("/admin/users").catch(() => ({ data: [] })))
      .then(r => setDeptUsers(r.data || []));
  }, [runId]);

  useEffect(() => {
    if (!runId) return;
    if (activeTab === "manual") {
      setLoading(true);
      api.get(`/reconciliation/runs/${runId}/manual-matches`)
        .then(r => { setManualGroups(r.data || []); setResults([]); setTotal((r.data || []).length); })
        .finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    setPage(0);
    const params = { match_type: activeTab, skip: 0, limit: PAGE_SIZE };
    if (debouncedQ) params.q = debouncedQ;
    if (sortSpec.length > 0) params.sort = sortSpec.map(s => `${s.path}:${s.dir}`).join(",");
    if (ruleFilter) params.rule_name = ruleFilter;
    api.get(`/reconciliation/runs/${runId}/results`, { params }).then(r => {
      const merged = mergeManualPairs(r.data.results);
      setResults(merged);
      setTotal(r.data.total);
      // Track all columns observed this tab (for the column selector)
      const sample = merged.find(x => x.row_a && x.row_b) || merged[0] || {};
      const ka = Object.keys(sample.row_a || {}).filter(k => !k.startsWith("_"));
      const kb = Object.keys(sample.row_b || {}).filter(k => !k.startsWith("_"));
      setAvailA(prev => Array.from(new Set([...prev, ...ka])));
      setAvailB(prev => Array.from(new Set([...prev, ...kb])));
    }).finally(() => setLoading(false));
  }, [runId, activeTab, debouncedQ, sortSpec, ruleFilter]);

  // When entering match mode, pull full unmatched lists for both sides (server-side search via debounced q)
  useEffect(() => {
    if (!matchMode) return;
    const paramsA = { match_type: "unmatched_a", limit: 1000 };
    const paramsB = { match_type: "unmatched_b", limit: 1000 };
    if (debouncedSplitA) paramsA.q = debouncedSplitA;
    if (debouncedSplitB) paramsB.q = debouncedSplitB;
    Promise.all([
      api.get(`/reconciliation/runs/${runId}/results`, { params: paramsA }),
      api.get(`/reconciliation/runs/${runId}/results`, { params: paramsB }),
    ]).then(([ra, rb]) => {
      setSplitPaneA(ra.data.results || []);
      setSplitPaneB(rb.data.results || []);
    });
  }, [matchMode, runId, debouncedSplitA, debouncedSplitB]);

  // Reset split pane selections + search when entering match mode
  useEffect(() => {
    if (!matchMode) return;
    setSelectedA(new Set()); setSelectedB(new Set());
    setSplitSearchA(""); setSplitSearchB("");
  }, [matchMode]);

  // -----------------------------------------------------------------------
  // Refreshers
  // -----------------------------------------------------------------------
  const refreshRun = async () => {
    const r = await api.get(`/reconciliation/runs/${runId}`);
    setRun(r.data);
    try {
      const rp = await api.get(`/reconciliation/runs/${runId}/rule-performance`);
      setRulePerf(rp.data);
    } catch {}
  };

  const refreshSplitPanes = async () => {
    const paramsA = { match_type: "unmatched_a", limit: 1000 };
    const paramsB = { match_type: "unmatched_b", limit: 1000 };
    if (debouncedSplitA) paramsA.q = debouncedSplitA;
    if (debouncedSplitB) paramsB.q = debouncedSplitB;
    const [ra, rb] = await Promise.all([
      api.get(`/reconciliation/runs/${runId}/results`, { params: paramsA }),
      api.get(`/reconciliation/runs/${runId}/results`, { params: paramsB }),
    ]);
    setSplitPaneA(ra.data.results || []);
    setSplitPaneB(rb.data.results || []);
  };

  const refreshManualGroups = async () => {
    const r = await api.get(`/reconciliation/runs/${runId}/manual-matches`);
    setManualGroups(r.data || []);
    setManualPage(0);
  };

  // -----------------------------------------------------------------------
  // Selection helpers
  // -----------------------------------------------------------------------
  const toggleSelect = (setter, set, id) => {
    const newSet = new Set(set);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setter(newSet);
  };

  const selectionStatus = () => {
    const sa = selectedA.size, sb = selectedB.size;
    if (sa === 0 && sb === 0) return { state: "empty", msg: "Select rows on both sides" };
    if (sa === 0 || sb === 0) return { state: "invalid", msg: "Select at least one row on each side" };
    if (sa > 1 && sb > 1) return { state: "invalid", msg: "Ambiguous many-to-many — split into multiple matches" };
    const kind = sa === 1 && sb === 1 ? "1:1" : sa === 1 ? `1:${sb}` : `${sa}:1`;
    return { state: "ok", msg: `${kind} match ready` };
  };

  const submitManualMatch = async () => {
    const s = selectionStatus();
    if (s.state !== "ok") return;
    setSubmittingMatch(true);
    setMatchError("");
    try {
      await api.post(`/reconciliation/runs/${runId}/manual-match`, {
        a_result_ids: Array.from(selectedA),
        b_result_ids: Array.from(selectedB),
        reason: manualReason,
        remarks: manualRemarks,
      });
      setSelectedA(new Set()); setSelectedB(new Set());
      setManualRemarks("");
      await Promise.all([refreshSplitPanes(), refreshRun(), refreshManualGroups()]);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      console.error("[manual-match] failed:", msg, e);
      setMatchError(`Manual match failed: ${msg}`);
    } finally {
      setSubmittingMatch(false);
    }
  };

  const unmatchGroup = async (mm_id) => {
    if (!window.confirm("Unmatch this group? Rows will be returned to Unmatched tabs.")) return;
    try {
      await api.post(`/reconciliation/runs/${runId}/unmatch/${mm_id}`, {});
      await Promise.all([refreshRun(), refreshManualGroups()]);
      if (matchMode) await refreshSplitPanes();
    } catch (e) {
      alert(`Unmatch failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // -----------------------------------------------------------------------
  // Flag submit
  // -----------------------------------------------------------------------
  const submitFlag = async () => {
    if (!flagRow) return;
    try {
      const payload = {
        run_id: runId,
        result_id: flagRow.id,
        side: activeTab === "unmatched_a" ? "a" : activeTab === "unmatched_b" ? "b" : "both",
        row_snapshot: flagRow.row_a || flagRow.row_b || {},
        notes: flagNotes,
        flag: flagStatus,
        assigned_to_email: flagAssignee || null,
      };
      await api.post("/exceptions/", payload);
      setFlaggedIds(prev => new Set([...prev, flagRow.id]));
      setFlagRow(null); setFlagNotes(""); setFlagStatus("review"); setFlagAssignee("");
    } catch (e) {
      alert(`Flag failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // -----------------------------------------------------------------------
  // Pagination
  // -----------------------------------------------------------------------
  const loadPage = async (newPage) => {
    setLoading(true);
    const skip = newPage * PAGE_SIZE;
    const params = { match_type: activeTab, skip, limit: PAGE_SIZE };
    if (debouncedQ) params.q = debouncedQ;
    if (sortSpec.length > 0) params.sort = sortSpec.map(s => `${s.path}:${s.dir}`).join(",");
    if (ruleFilter) params.rule_name = ruleFilter;
    const res = await api.get(`/reconciliation/runs/${runId}/results`, { params });
    setResults(mergeManualPairs(res.data.results));
    setTotal(res.data.total);
    setPage(newPage);
    setLoading(false);
  };

  // -----------------------------------------------------------------------
  // Downloads
  // -----------------------------------------------------------------------
  const handleDownload = (type) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reconciliation/runs/${runId}/download?match_type=${type}`;
    window.open(url, "_blank");
  };
  const [htmlPromptOpen, setHtmlPromptOpen] = useState(false);
  const handleHtmlReport = () => setHtmlPromptOpen(true);
  const doHtmlReport = (includePartial) => {
    const qs = includePartial ? "?include_partial=true" : "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reconciliation/runs/${runId}/report${qs}`;
    window.open(url, "_blank");
    setHtmlPromptOpen(false);
  };
  const handlePdfReport = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reconciliation/runs/${runId}/pdf`;
    window.open(url, "_blank");
  };

  // #2 Partial approve/reject
  const submitPartialDecision = async () => {
    const result_ids = Array.from(selectedPartial);
    if (!result_ids.length || !partialDecisionOpen) return;
    try {
      await api.post(`/reconciliation/runs/${runId}/partial-decision`, {
        result_ids, decision: partialDecisionOpen, remarks: partialRemarks,
      });
      setSelectedPartial(new Set());
      setPartialDecisionOpen(null); setPartialRemarks("");
      // Reload results + summary + perf
      const [runRes, perfRes, listRes] = await Promise.all([
        api.get(`/reconciliation/runs/${runId}`),
        api.get(`/reconciliation/runs/${runId}/rule-performance`),
        api.get(`/reconciliation/runs/${runId}/results`, { params: { match_type: activeTab, skip: 0, limit: PAGE_SIZE } }),
      ]);
      setRun(runRes.data); setRulePerf(perfRes.data);
      setResults(mergeManualPairs(listRes.data.results)); setTotal(listRes.data.total);
    } catch (e) {
      alert(`Decision failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // E7: Ignore unmatched rows (1:0 dismiss)
  const submitIgnore = async () => {
    const result_ids = Array.from(selectedIgnore);
    if (!result_ids.length) return;
    setIgnoring(true);
    try {
      await api.post(`/reconciliation/runs/${runId}/ignore-rows`, {
        result_ids, reason: ignoreReason, remarks: ignoreRemarks,
      });
      setSelectedIgnore(new Set());
      setIgnoreModalOpen(false);
      setIgnoreRemarks("");
      const [runRes, listRes] = await Promise.all([
        api.get(`/reconciliation/runs/${runId}`),
        api.get(`/reconciliation/runs/${runId}/results`, { params: { match_type: activeTab, skip: 0, limit: PAGE_SIZE } }),
      ]);
      setRun(runRes.data);
      setResults(mergeManualPairs(listRes.data.results));
      setTotal(listRes.data.total);
    } catch (e) {
      alert(`Ignore failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setIgnoring(false);
    }
  };

  const undoIgnore = async (resultId) => {
    try {
      await api.post(`/reconciliation/runs/${runId}/unignore/${resultId}`);
      const [runRes, listRes] = await Promise.all([
        api.get(`/reconciliation/runs/${runId}`),
        api.get(`/reconciliation/runs/${runId}/results`, { params: { match_type: activeTab, skip: 0, limit: PAGE_SIZE } }),
      ]);
      setRun(runRes.data);
      setResults(mergeManualPairs(listRes.data.results));
      setTotal(listRes.data.total);
    } catch (e) {
      alert(`Undo ignore failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // #4 Save current column prefs as team default for this rule set
  const saveTeamPrefs = async () => {
    if (!run?.rule_set_id) return;
    setSavingPrefs(true);
    try {
      const payload = {};
      ["matched", "partial", "unmatched_a", "unmatched_b", "manual"].forEach(t => {
        const p = colPrefs[t];
        if (p && (p.a || p.b)) payload[t] = { a: p.a || [], b: p.b || [] };
      });
      // If nothing explicit yet, save the current effective view
      if (Object.keys(payload).length === 0) {
        payload[activeTab] = { a: effectiveA, b: effectiveB };
      }
      await api.put(`/rule-sets/${run.rule_set_id}/display-prefs`, payload);
      setTeamPrefs(payload);
      alert("Saved as team default for this rule set.");
    } catch (e) {
      alert(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSavingPrefs(false);
    }
  };

  if (!run) return (
    <Layout>
      <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <div style={{ borderColor: "var(--sage)", width: "2rem", height: "2rem", border: "2px solid var(--sage)", borderTop: "2px solid transparent", borderRadius: "9999px", animation: "spin 0.8s linear infinite" }} />
      </div>
    </Layout>
  );

  const s = run.summary || {};

  // ---------------- Columns for main table ----------------
  const getColumns = () => {
    if (results.length === 0) return { a: [], b: [] };
    // Pick a sample row that has both sides populated (handles one-sided manual docs)
    const sample = results.find(r => r.row_a && r.row_b) || results[0];
    const a = Object.keys(sample?.row_a || {}).filter(k => !k.startsWith("_"));
    const b = Object.keys(sample?.row_b || {}).filter(k => !k.startsWith("_"));
    return { a, b };
  };
  const { a: allA, b: allB } = getColumns();


  // Effective column selection for the current tab
  // Fallback chain: user localStorage > team (server-side) > first 5 per side.
  const DEFAULT_COL_LIMIT = 5;
  const tabPrefs = colPrefs[activeTab] || {};
  const teamTab = (teamPrefs || {})[activeTab] || {};
  const effectiveA = tabPrefs.a
    ? tabPrefs.a.filter(k => availA.includes(k))
    : (teamTab.a ? teamTab.a.filter(k => availA.includes(k)) : availA.slice(0, DEFAULT_COL_LIMIT));
  const effectiveB = tabPrefs.b
    ? tabPrefs.b.filter(k => availB.includes(k))
    : (teamTab.b ? teamTab.b.filter(k => availB.includes(k)) : availB.slice(0, DEFAULT_COL_LIMIT));

  const buildColumns = () => {
    if (activeTab === "unmatched_a") return effectiveA.map(k => ({ side: "A", key: k }));
    if (activeTab === "unmatched_b") return effectiveB.map(k => ({ side: "B", key: k }));
    return [...effectiveA.map(k => ({ side: "A", key: k })), ...effectiveB.map(k => ({ side: "B", key: k }))];
  };
  const columns = buildColumns();
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ---------------- Filter split panes (now handled inside MatchSplitPane) ----------------
  const sel = selectionStatus();
  const canShowMatchMode = activeTab === "unmatched_a" || activeTab === "unmatched_b";

  const pagedManualGroups = manualGroups.slice(manualPage * MANUAL_PAGE_SIZE, (manualPage + 1) * MANUAL_PAGE_SIZE);
  const manualPages = Math.max(1, Math.ceil(manualGroups.length / MANUAL_PAGE_SIZE));

  // ---------------- Tab search filter (mirrors Match-Mode search) ----------------
  const flattenResultRow = (r) => {
    const out = {};
    if (r?.rule_name) out.rule_name = r.rule_name;
    if (r?.match_type) out.match_type = r.match_type;
    if (r?.mismatch_reason) out.mismatch_reason = r.mismatch_reason;
    Object.entries(r?.row_a || {}).forEach(([k, v]) => { if (!k.startsWith("_")) out[`A:${k}`] = v; });
    Object.entries(r?.row_b || {}).forEach(([k, v]) => { if (!k.startsWith("_AGG") && !k.startsWith("_")) out[`B:${k}`] = v; });
    return out;
  };
  const flattenManualGroup = (g) => {
    const out = { reason: REASON_LABEL[g.reason] || g.reason, remarks: g.remarks || "", matched_by: g.matched_by_email || "" };
    (g.a_rows || []).forEach((r, i) => {
      Object.entries(r.row_a || {}).forEach(([k, v]) => { if (!k.startsWith("_")) out[`A${i}:${k}`] = v; });
    });
    (g.b_rows || []).forEach((r, i) => {
      Object.entries(r.row_b || {}).forEach(([k, v]) => { if (!k.startsWith("_")) out[`B${i}:${k}`] = v; });
    });
    return out;
  };
  const filterCols = (() => {
    const cols = new Set();
    if (activeTab === "manual") {
      cols.add("reason"); cols.add("remarks"); cols.add("matched_by");
      availA.forEach(k => cols.add(`A0:${k}`));
      availB.forEach(k => cols.add(`B0:${k}`));
    } else {
      if (activeTab === "matched" || activeTab === "partial") cols.add("rule_name");
      if (activeTab === "partial") cols.add("mismatch_reason");
      availA.forEach(k => cols.add(`A:${k}`));
      availB.forEach(k => cols.add(`B:${k}`));
    }
    return [...cols];
  })();
  // Field-specific client filter only (free text goes to server via debouncedQ)
  const clientFilter = { ...tabFilter, text: "" };
  const filteredResults = results.filter(r => rowPassesTabFilter(flattenResultRow(r), clientFilter));
  const filteredManualGroups = manualGroups.filter(g => rowPassesTabFilter(flattenManualGroup(g), tabFilter));
  const filteredPagedManual = filteredManualGroups.slice(manualPage * MANUAL_PAGE_SIZE, (manualPage + 1) * MANUAL_PAGE_SIZE);
  const filteredManualPages = Math.max(1, Math.ceil(filteredManualGroups.length / MANUAL_PAGE_SIZE));

  return (
    <Layout>
      <div className="animate-in">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex" }}>
              <ArrowLeft size={20} strokeWidth={1.5} />
            </button>
            <div>
              <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.5rem", color: "var(--text-primary)", margin: 0 }}>{run.name}</h1>
              <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", margin: 0 }}>
                {run.rule_set_name} · {run.file_a_name} vs {run.file_b_name} · {new Date(run.created_at).toLocaleString()}
                {run.rerun_of && (
                  <span data-testid="rerun-lineage-pill" style={{ marginLeft: "0.5rem", fontSize: "0.7rem", padding: "0.1rem 0.45rem", background: "#EDE9FE", color: "#5B21B6", borderRadius: "9999px", border: "1px solid #C4B5FD", cursor: "pointer" }}
                    onClick={() => navigate(`/results/${run.rerun_of}`)}
                    title="Open the original run">
                    Rerun of ↩
                  </span>
                )}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button data-testid="report-btn"
              onClick={() => navigate(`/reports?run=${runId}`)}
              className="btn-secondary"
              title="Build a Debit/Credit report for this run"
              style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.875rem" }}>
              <BarChart3 size={14} /> Report
            </button>
            <button data-testid="rerun-btn"
              onClick={async () => {
                if (!window.confirm("Re-run this reconciliation with the same files, transform sets and rule set? A new versioned run will be created.")) return;
                try {
                  const r = await api.post(`/reconciliation/runs/${runId}/rerun`);
                  if (r.data?.run_id) navigate(`/results/${r.data.run_id}`);
                } catch (e) {
                  alert(`Rerun failed: ${e.response?.data?.detail || e.message}`);
                }
              }}
              className="btn-secondary"
              title="Create a new run with the same configuration"
              style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.875rem" }}>
              <RotateCw size={14} /> Rerun
            </button>
            {canShowMatchMode && (
              <button
                data-testid="match-mode-toggle"
                onClick={() => setMatchMode(m => !m)}
                className={matchMode ? "btn-primary" : "btn-secondary"}
                style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.875rem" }}>
                <Link2 size={14} /> {matchMode ? "Exit Match Mode" : "Match Mode"}
              </button>
            )}
            <button data-testid="download-pdf-btn" className="btn-secondary" onClick={handlePdfReport}
              style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.875rem" }}>
              <FileText size={14} /> PDF
            </button>
            <button data-testid="download-html-btn" className="btn-secondary" onClick={handleHtmlReport}
              style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.875rem" }}>
              <FileText size={14} /> HTML
            </button>
            <button data-testid="download-all-btn" className="btn-secondary" onClick={() => handleDownload("all")}
              style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.875rem" }}>
              <Download size={14} /> All CSV
            </button>
            <button data-testid="download-tab-btn" className="btn-primary" onClick={() => handleDownload(activeTab)}
              style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.875rem" }}>
              <Download size={14} /> Tab CSV
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
          <SummaryCard label="File A Rows" value={s.total_a ?? "—"} color="var(--text-primary)" />
          <SummaryCard label="File B Rows" value={s.total_b ?? "—"} color="var(--text-primary)" />
          <SummaryCard label="Matched" value={s.matched ?? "—"} color="#065F46" icon={CheckCircle} />
          <SummaryCard label="Manual" value={rulePerf?.totals?.manual_groups ?? s.manual ?? 0} color="#5B21B6" icon={Link2} sub="manually reconciled" />
          <SummaryCard label="Partial" value={s.partial ?? 0} color="#92400E" icon={Flag} sub="needs review" />
          <SummaryCard label="Match Rate" value={`${s.match_rate ?? 0}%`}
            sub={((s.ignored_a || 0) + (s.ignored_b || 0)) > 0
              ? `A:${s.unmatched_a ?? 0} · B:${s.unmatched_b ?? 0} unmatched · ignored excluded`
              : `A:${s.unmatched_a ?? 0} · B:${s.unmatched_b ?? 0} unmatched`}
            color={s.match_rate >= 80 ? "#065F46" : s.match_rate >= 50 ? "#92400E" : "#991B1B"}
            icon={AlertTriangle} />
          <SummaryCard
            label="Effective Coverage"
            value={`${s.effective_coverage ?? 0}%`}
            sub={`matched + ignored / total A${((s.ignored_a || 0) > 0) ? ` · ${s.ignored_a} ignored` : ""}`}
            color={s.effective_coverage >= 90 ? "#065F46" : s.effective_coverage >= 70 ? "#92400E" : "#991B1B"}
            icon={ShieldCheck}
            testid="summary-effective-coverage"
          />
        </div>

        {/* F2 + E4 — Rule performance + tolerance summary */}
        {rulePerf && (rulePerf.rules.length > 0 || rulePerf.totals.manual_groups > 0 || rulePerf.totals.unmatched_a > 0 || rulePerf.totals.unmatched_b > 0) && (
          <div data-testid="rule-performance-card" className="recon-card" style={{ padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1rem", margin: 0 }}>Rule Performance</h3>
              {rulePerf.totals.tolerance_matched > 0 && (
                <span data-testid="tolerance-summary" style={{ fontSize: "0.72rem", color: "#5B21B6", background: "#EDE9FE", padding: "0.2rem 0.55rem", borderRadius: "9999px", border: "1px solid #DDD6FE" }}>
                  Tolerance-matched: {rulePerf.totals.tolerance_matched} rows
                </span>
              )}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr>
                  {["Rule", "Type", "Matches", "% of A", "Bar"].map(h => <th key={h} className="table-header" style={{ textAlign: "left", fontSize: "0.65rem" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rulePerf.rules.map(r => (
                  <tr key={r.rule_name} data-testid={`rule-perf-row-${r.rule_name}`} style={{ borderBottom: "1px solid var(--border-default)" }}>
                    <td style={{ padding: "0.4rem 0.5rem", fontWeight: 500 }}>{r.rule_name}</td>
                    <td style={{ padding: "0.4rem 0.5rem", color: "var(--text-muted)", fontSize: "0.75rem" }}>{r.match_type}</td>
                    <td style={{ padding: "0.4rem 0.5rem", fontFamily: "JetBrains Mono, monospace" }}>{r.total}</td>
                    <td style={{ padding: "0.4rem 0.5rem", color: "#065F46", fontWeight: 600 }}>{r.pct_of_a}%</td>
                    <td style={{ padding: "0.4rem 0.5rem", width: "30%" }}>
                      <div style={{ background: "#EDEBE4", borderRadius: "9999px", height: "6px", overflow: "hidden" }}>
                        <div style={{ background: "#5C7A5E", height: "100%", width: `${Math.min(100, r.pct_of_a)}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
                <tr data-testid="rule-perf-manual" style={{ borderTop: "2px solid var(--border-default)", background: "#FAFAF7" }}>
                  <td style={{ padding: "0.4rem 0.5rem", fontWeight: 600, color: "#5B21B6" }}>Manual</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: "var(--text-muted)", fontSize: "0.75rem" }}>manual groups</td>
                  <td style={{ padding: "0.4rem 0.5rem", fontFamily: "JetBrains Mono, monospace" }}>{rulePerf.totals.manual_groups}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: "#5B21B6", fontWeight: 600 }}>{rulePerf.totals.manual_pct ?? 0}%</td>
                  <td style={{ padding: "0.4rem 0.5rem" }}>
                    <div style={{ background: "#EDEBE4", borderRadius: "9999px", height: "6px", overflow: "hidden" }}>
                      <div style={{ background: "#5B21B6", height: "100%", width: `${Math.min(100, rulePerf.totals.manual_pct ?? 0)}%` }} />
                    </div>
                  </td>
                </tr>
                <tr data-testid="rule-perf-unmatched" style={{ background: "#FFFBEB" }}>
                  <td style={{ padding: "0.4rem 0.5rem", fontWeight: 600, color: "#991B1B" }}>Unmatched</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: "var(--text-muted)", fontSize: "0.75rem" }}>A + B</td>
                  <td style={{ padding: "0.4rem 0.5rem", fontFamily: "JetBrains Mono, monospace" }}>{rulePerf.totals.unmatched_a} / {rulePerf.totals.unmatched_b}</td>
                  <td style={{ padding: "0.4rem 0.5rem", color: "#991B1B", fontWeight: 600 }}>
                    {rulePerf.totals.total_a ? Math.round((rulePerf.totals.unmatched_a / rulePerf.totals.total_a) * 10000) / 100 : 0}%
                  </td>
                  <td style={{ padding: "0.4rem 0.5rem" }}>
                    <div style={{ background: "#EDEBE4", borderRadius: "9999px", height: "6px", overflow: "hidden" }}>
                      <div style={{ background: "#DC2626", height: "100%", width: `${rulePerf.totals.total_a ? (rulePerf.totals.unmatched_a / rulePerf.totals.total_a) * 100 : 0}%` }} />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Tabs & body */}
        <div className="recon-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1rem" }}>
            <div style={{ display: "flex" }}>
              {TABS.map(t => (
                <button key={t.id} data-testid={`results-tab-${t.id}`}
                  onClick={() => { setActiveTab(t.id); setMatchMode(false); setTabFilter(EMPTY_TAB_FILTER); }}
                  style={{
                    padding: "0.875rem 1rem", fontSize: "0.875rem", fontWeight: 500,
                    border: "none", background: "none", cursor: "pointer",
                    borderBottom: activeTab === t.id ? "2px solid var(--sage)" : "2px solid transparent",
                    color: activeTab === t.id ? "var(--sage)" : "var(--text-muted)", transition: "all 0.15s",
                    display: "flex", alignItems: "center", gap: "0.5rem",
                  }}>
                  {t.label}
                  <span className={t.badge} style={{ fontSize: "0.7rem" }}>
                    {t.id === "matched" ? ((s.matched || 0) + (rulePerf?.totals?.manual_groups ?? s.manual ?? 0))
                      : t.id === "partial" ? (s.partial || 0)
                      : t.id === "unmatched_a" ? s.unmatched_a
                      : t.id === "unmatched_b" ? s.unmatched_b
                      : t.id === "ignored" ? ((s.ignored_a || 0) + (s.ignored_b || 0))
                      : (rulePerf?.totals?.manual_groups ?? s.manual ?? 0)}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              {activeTab !== "manual" && !matchMode && (
                <button
                  data-testid="column-selector-btn"
                  onClick={() => setColsOpen(true)}
                  className="btn-secondary"
                  style={{ fontSize: "0.75rem", padding: "0.25rem 0.625rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <Columns3 size={12} /> Columns ({effectiveA.length + effectiveB.length}/{availA.length + availB.length})
                </button>
              )}
              {!matchMode && activeTab !== "manual" && totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                  Page {page + 1} of {totalPages}
                  <button onClick={() => loadPage(page - 1)} disabled={page === 0} style={{ background: "none", border: "none", cursor: "pointer", opacity: page === 0 ? 0.3 : 1 }}><ChevronLeft size={14} /></button>
                  <button onClick={() => loadPage(page + 1)} disabled={page >= totalPages - 1} style={{ background: "none", border: "none", cursor: "pointer", opacity: page >= totalPages - 1 ? 0.3 : 1 }}><ChevronRight size={14} /></button>
                </div>
              )}
            </div>
          </div>

          {/* Drill-down chip: rule filter from Rule Impact bar */}
          {ruleFilter && (
            <div
              data-testid="rule-filter-chip-bar"
              style={{
                display: "flex", alignItems: "center", gap: "0.6rem",
                padding: "0.55rem 1rem",
                background: "var(--sage-light)",
                borderBottom: "1px solid var(--sage-border)",
                fontSize: "0.78rem", color: "var(--text-secondary)",
              }}>
              <span style={{ fontWeight: 500 }}>Filtered to rule:</span>
              <span
                data-testid="rule-filter-chip"
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.4rem",
                  background: "white", color: "var(--sage)",
                  padding: "0.2rem 0.6rem", borderRadius: 9999,
                  border: "1px solid var(--sage-border)", fontWeight: 600,
                }}>
                {ruleFilter}
                <button
                  data-testid="rule-filter-clear"
                  onClick={clearRuleFilter}
                  title="Clear rule filter"
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "var(--sage)", padding: 0, display: "inline-flex",
                  }}>
                  <X size={13} />
                </button>
              </span>
              <span style={{ color: "var(--text-muted)" }}>
                {total} row{total === 1 ? "" : "s"} on this tab · drilled from Rule Impact bar
              </span>
            </div>
          )}

          {/* ================= MATCH MODE SPLIT PANE ================= */}
          {matchMode && canShowMatchMode ? (
            <MatchSplitPane
              rowsA={splitPaneA} rowsB={splitPaneB}
              labelA="File A" labelB="File B"
              colorA="var(--sage)" colorB="#1E40AF"
              borderColorA="var(--sage-border)" borderColorB="#BFDBFE"
              selectedA={selectedA} setSelectedA={setSelectedA}
              selectedB={selectedB} setSelectedB={setSelectedB}
              searchTextA={splitSearchA} onSearchTextChangeA={setSplitSearchA}
              searchTextB={splitSearchB} onSearchTextChangeB={setSplitSearchB}
            />
          ) : activeTab === "manual" ? (
            // ============== MANUALLY RECONCILED TAB ==============
            <div data-testid="manual-tab-pane" style={{ background: "#FAFAF7" }}>
              {manualGroups.length > 0 && (
                <TabSearchFilter
                  filter={tabFilter}
                  onChange={(f) => { setTabFilter(f); setManualPage(0); }}
                  columns={filterCols}
                  testidPrefix="results-tab-filter"
                  visible={filteredManualGroups.length}
                  total={manualGroups.length}
                />
              )}
              <div style={{ padding: "0.75rem 1rem" }}>
              {loading ? (
                <div style={{ padding: "3rem", textAlign: "center" }}>
                  <div style={{ width: "1.5rem", height: "1.5rem", border: "2px solid var(--sage)", borderTop: "2px solid transparent", borderRadius: "9999px", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
                </div>
              ) : manualGroups.length === 0 ? (
                <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                  No manual matches yet. Go to <strong>Unmatched (File A)</strong> or <strong>Unmatched (File B)</strong> and click <em>Match Mode</em> to create one.
                </div>
              ) : filteredManualGroups.length === 0 ? (
                <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>No manual matches match this filter</div>
              ) : (
                <>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {filteredPagedManual.map(g => (
                    <div key={g.manual_match_id} data-testid={`manual-group-${g.manual_match_id}`}
                      style={{ background: "white", border: "1px solid var(--sage-border)", borderRadius: "0.5rem", padding: "0.875rem 1rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.625rem" }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                            <span style={{ fontSize: "0.75rem", background: "#EDE9FE", color: "#5B21B6", padding: "0.125rem 0.5rem", borderRadius: "9999px", fontWeight: 600 }}>
                              {g.a_rows.length}:{g.b_rows.length} manual
                            </span>
                            <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>{REASON_LABEL[g.reason] || g.reason}</span>
                          </div>
                          {g.remarks && <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)", fontStyle: "italic", marginBottom: "0.25rem" }}>"{g.remarks}"</div>}
                          <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                            by {g.matched_by_email} · {g.matched_at ? new Date(g.matched_at).toLocaleString() : "—"}
                          </div>
                        </div>
                        <button data-testid={`unmatch-btn-${g.manual_match_id}`}
                          onClick={() => unmatchGroup(g.manual_match_id)}
                          className="btn-secondary"
                          style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.75rem", padding: "0.3rem 0.65rem", color: "#991B1B", borderColor: "#FCA5A5" }}>
                          <Unlink size={12} /> Unmatch
                        </button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", fontSize: "0.75rem" }}>
                        <div style={{ border: "1px solid var(--border-default)", borderRadius: "0.375rem", padding: "0.5rem", background: "#F9FBF9" }}>
                          <div style={{ fontSize: "0.68rem", color: "var(--sage)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.3rem" }}>File A ({g.a_rows.length})</div>
                          {g.a_rows.map(r => (
                            <div key={r.id} style={{ padding: "0.2rem 0", borderBottom: "1px solid rgba(226,225,217,0.5)", fontFamily: "JetBrains Mono, monospace", fontSize: "0.7rem" }}>
                              {Object.entries(r.row_a || {}).slice(0, 5).map(([k, v]) => (
                                <span key={k} style={{ marginRight: "0.5rem" }}>
                                  <span style={{ color: "var(--text-muted)" }}>{k}:</span>{" "}
                                  <TransformCell original={v} transformed={r.row_a_transformed?.[k]} />
                                </span>
                              ))}
                            </div>
                          ))}
                        </div>
                        <div style={{ border: "1px solid var(--border-default)", borderRadius: "0.375rem", padding: "0.5rem", background: "#F9FBFD" }}>
                          <div style={{ fontSize: "0.68rem", color: "#1E40AF", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.3rem" }}>File B ({g.b_rows.length})</div>
                          {g.b_rows.map(r => (
                            <div key={r.id} style={{ padding: "0.2rem 0", borderBottom: "1px solid rgba(226,225,217,0.5)", fontFamily: "JetBrains Mono, monospace", fontSize: "0.7rem" }}>
                              {Object.entries(r.row_b || {}).slice(0, 5).map(([k, v]) => (
                                <span key={k} style={{ marginRight: "0.5rem" }}>
                                  <span style={{ color: "var(--text-muted)" }}>{k}:</span>{" "}
                                  <TransformCell original={v} transformed={r.row_b_transformed?.[k]} />
                                </span>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {filteredManualGroups.length > MANUAL_PAGE_SIZE && (
                  <div data-testid="manual-pager" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.5rem 0.75rem", marginTop: "0.5rem", borderTop: "1px solid var(--border-default)", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                    <span>Showing {manualPage * MANUAL_PAGE_SIZE + 1}–{Math.min((manualPage + 1) * MANUAL_PAGE_SIZE, filteredManualGroups.length)} of {filteredManualGroups.length} groups</span>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <button onClick={() => setManualPage(p => Math.max(0, p - 1))} disabled={manualPage === 0} className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.2rem 0.55rem", opacity: manualPage === 0 ? 0.4 : 1, display: "flex", alignItems: "center", gap: "0.25rem" }}><ChevronLeft size={12} /> Prev</button>
                      <span>Page {manualPage + 1} / {filteredManualPages}</span>
                      <button onClick={() => setManualPage(p => Math.min(filteredManualPages - 1, p + 1))} disabled={manualPage >= filteredManualPages - 1} className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.2rem 0.55rem", opacity: manualPage >= filteredManualPages - 1 ? 0.4 : 1, display: "flex", alignItems: "center", gap: "0.25rem" }}>Next <ChevronRight size={12} /></button>
                    </div>
                  </div>
                )}
                </>
              )}
              </div>
            </div>
          ) : (
            // ============== DEFAULT TABLE ==============
            <>
            {(results.length > 0 || debouncedQ) && (
              <TabSearchFilter
                filter={tabFilter}
                onChange={setTabFilter}
                columns={filterCols}
                testidPrefix="results-tab-filter"
                visible={filteredResults.length}
                total={total}
              />
            )}
            {filterCols.length > 0 && (
              <div style={{ padding: "0.4rem 0.875rem", borderBottom: "1px solid var(--border-default)", background: "var(--bg-subtle, #FAFAF7)", display: "flex", flexWrap: "wrap" }}>
                <SortPicker
                  columns={filterCols.map(c => ({ value: c, label: c }))}
                  spec={sortSpec}
                  setSpec={setSortSpec}
                  testidPrefix="results-sort"
                />
              </div>
            )}
            <div style={{ overflowX: "auto", maxHeight: "60vh", overflowY: "auto" }}>
              {loading ? (
                <div style={{ padding: "4rem", textAlign: "center" }}>
                  <div style={{ width: "1.5rem", height: "1.5rem", border: "2px solid var(--sage)", borderTop: "2px solid transparent", borderRadius: "9999px", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
                </div>
              ) : results.length === 0 ? (
                <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>
                  {(debouncedQ || tabFilter.value) ? "No records match this filter" : "No records in this category"}
                </div>
              ) : filteredResults.length === 0 ? (
                <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>No records match this filter</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {activeTab === "partial" && (
                        <th className="table-header" style={{ width: "32px" }}>
                          <input data-testid="partial-select-all" type="checkbox"
                            checked={filteredResults.length > 0 && filteredResults.every(r => selectedPartial.has(r.id))}
                            onChange={() => {
                              const allSelected = filteredResults.length > 0 && filteredResults.every(r => selectedPartial.has(r.id));
                              const next = new Set(selectedPartial);
                              if (allSelected) filteredResults.forEach(r => next.delete(r.id));
                              else filteredResults.forEach(r => next.add(r.id));
                              setSelectedPartial(next);
                            }} />
                        </th>
                      )}
                      {(activeTab === "unmatched_a" || activeTab === "unmatched_b") && (
                        <th className="table-header" style={{ width: "32px" }}>
                          <input data-testid="ignore-select-all" type="checkbox"
                            checked={filteredResults.length > 0 && filteredResults.every(r => selectedIgnore.has(r.id))}
                            onChange={() => {
                              const allSelected = filteredResults.length > 0 && filteredResults.every(r => selectedIgnore.has(r.id));
                              const next = new Set(selectedIgnore);
                              if (allSelected) filteredResults.forEach(r => next.delete(r.id));
                              else filteredResults.forEach(r => next.add(r.id));
                              setSelectedIgnore(next);
                            }} />
                        </th>
                      )}
                      {(activeTab === "matched" || activeTab === "partial") && <th className="table-header">Rule</th>}
                      {activeTab === "ignored" && <th className="table-header">Ignored</th>}
                      {columns.map((c, ci) => {
                        const prevSide = ci > 0 ? columns[ci - 1].side : null;
                        const needsDivider = prevSide && prevSide !== c.side;
                        const tint = c.side === "A" ? "#F0F7F0" : "#EEF4FE";
                        const color = c.side === "A" ? "var(--sage)" : "#1E40AF";
                        return (
                          <th key={`${c.side}-${c.key}`} className="table-header"
                            style={{
                              background: tint, color,
                              borderLeft: needsDivider ? "3px solid #94A3B8" : undefined,
                            }}>
                            <span style={{ fontSize: "0.65rem", opacity: 0.8, marginRight: "0.25rem" }}>{c.side}</span>
                            {c.key}
                          </th>
                        );
                      })}
                      {activeTab === "partial" && <th className="table-header">Reason</th>}
                      {activeTab !== "matched" && <th className="table-header" style={{ width: "60px" }}>Flag</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((r, i) => {
                      const isExpanded = expandedRow === i;
                      const isMatched = activeTab === "matched";
                      const isPartial = activeTab === "partial";
                      const isFlagged = flaggedIds.has(r.id);
                      return (
                        <React.Fragment key={r.id || i}>
                          <tr
                            className="table-row"
                            data-testid={`result-row-${i}`}
                            onMouseEnter={(e) => { if (isMatched || isPartial) { setHoveredRow(i); setTooltipPos({ x: e.clientX, y: e.clientY }); } }}
                            onMouseMove={(e) => { if ((isMatched || isPartial) && hoveredRow === i) setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                            onMouseLeave={() => setHoveredRow(null)}
                            onClick={() => (isMatched || isPartial) && setExpandedRow(isExpanded ? null : i)}
                            style={{ position: "relative", cursor: (isMatched || isPartial) ? "pointer" : "default", background: isExpanded ? "var(--sage-light)" : (isPartial ? "#FFFBEB" : undefined) }}
                          >
                            {isPartial && (
                              <td className="table-cell" style={{ width: "32px" }} onClick={e => e.stopPropagation()}>
                                <input data-testid={`partial-check-${r.id}`} type="checkbox"
                                  checked={selectedPartial.has(r.id)}
                                  onChange={() => {
                                    const s = new Set(selectedPartial);
                                    if (s.has(r.id)) s.delete(r.id); else s.add(r.id);
                                    setSelectedPartial(s);
                                  }} />
                              </td>
                            )}
                            {(activeTab === "unmatched_a" || activeTab === "unmatched_b") && (
                              <td className="table-cell" style={{ width: "32px" }} onClick={e => e.stopPropagation()}>
                                <input data-testid={`ignore-check-${r.id}`} type="checkbox"
                                  checked={selectedIgnore.has(r.id)}
                                  onChange={() => {
                                    const s = new Set(selectedIgnore);
                                    if (s.has(r.id)) s.delete(r.id); else s.add(r.id);
                                    setSelectedIgnore(s);
                                  }} />
                              </td>
                            )}
                            {activeTab === "ignored" && (
                              <td className="table-cell">
                                <span data-testid={`ignored-badge-${i}`} style={{ fontSize: "0.72rem", background: "#F3F4F6", color: "#4B5563", padding: "0.15rem 0.55rem", borderRadius: "9999px", border: "1px solid #D1D5DB", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                                  Ignored
                                </span>
                                <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>
                                  {r.ignore_reason || ""}{r.ignore_remarks ? ` · ${r.ignore_remarks}` : ""}
                                </div>
                                <button data-testid={`unignore-${r.id}`} onClick={e => { e.stopPropagation(); undoIgnore(r.id); }}
                                  style={{ marginTop: "0.25rem", background: "none", border: "1px solid var(--border-default)", padding: "0.15rem 0.45rem", borderRadius: 6, fontSize: "0.68rem", cursor: "pointer", color: "var(--text-secondary)" }}>
                                  Undo
                                </button>
                              </td>
                            )}
                            {(isMatched || isPartial) && (
                              <td className="table-cell">
                                {r.match_type === "manual" ? (
                                  <span data-testid={`rule-badge-manual-${i}`} style={{ fontSize: "0.75rem", background: "#EDE9FE", color: "#5B21B6", padding: "0.125rem 0.5rem", borderRadius: "9999px", border: "1px solid #DDD6FE", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                                    <Link2 size={10} /> Manual
                                  </span>
                                ) : (
                                  <span style={{ fontSize: "0.75rem", background: r.match_type === "matched_aggregate" ? "#EDE9FE" : isPartial ? "#FEF3C7" : "var(--sage-light)", color: r.match_type === "matched_aggregate" ? "#5B21B6" : isPartial ? "#92400E" : "var(--sage)", padding: "0.125rem 0.5rem", borderRadius: "9999px", border: `1px solid ${r.match_type === "matched_aggregate" ? "#A78BFA" : isPartial ? "#FDE68A" : "var(--sage-border)"}` }}>
                                    {r.rule_name}{r.match_type === "matched_aggregate" ? " [AGG]" : isPartial ? " [PARTIAL]" : ""}
                                  </span>
                                )}
                              </td>
                            )}
                            {columns.map((c, ci) => {
                              const val = c.side === "A" ? r.row_a?.[c.key] : r.row_b?.[c.key];
                              const tVal = c.side === "A" ? r.row_a_transformed?.[c.key] : r.row_b_transformed?.[c.key];
                              const prevSide = ci > 0 ? columns[ci - 1].side : null;
                              const needsDivider = prevSide && prevSide !== c.side;
                              const tint = c.side === "A" ? "rgba(92,122,94,0.04)" : "rgba(30,64,175,0.04)";
                              return (
                                <td key={`${c.side}-${c.key}`} className="table-cell"
                                  style={{
                                    fontFamily: "JetBrains Mono, monospace", fontSize: "0.8rem",
                                    background: isExpanded ? undefined : tint,
                                    borderLeft: needsDivider ? "3px solid #94A3B8" : undefined,
                                  }}>
                                  <TransformCell original={val} transformed={tVal} />
                                </td>
                              );
                            })}
                            {activeTab === "partial" && (
                              <td className="table-cell" style={{ fontSize: "0.72rem", color: "#92400E", maxWidth: "240px" }}>
                                {r.mismatch_reason || "—"}
                              </td>
                            )}
                            {activeTab !== "matched" && (
                              <td className="table-cell" style={{ width: "60px" }}>
                                <button
                                  data-testid={`flag-row-${i}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFlagRow(r);
                                    setFlagNotes("");
                                    setFlagStatus("review");
                                  }}
                                  style={{ background: "none", border: "none", cursor: "pointer", color: isFlagged ? "#DC2626" : "var(--text-muted)", padding: "0.25rem" }}
                                  title={isFlagged ? "Already flagged" : "Flag as exception"}
                                >
                                  <Flag size={14} fill={isFlagged ? "#DC2626" : "none"} />
                                </button>
                              </td>
                            )}
                          </tr>
                          {isExpanded && (isMatched || isPartial) && (
                            <tr>
                              <td colSpan={columns.length + 1 + (isPartial ? 2 : 0) + (activeTab !== "matched" ? 1 : 0)} style={{ padding: "0.75rem 1rem", background: isPartial ? "#FFFBEB" : "#F0F7F0", borderBottom: "1px solid var(--border-default)" }}>
                                {isPartial && r.mismatch_reason && (
                                  <div style={{ marginBottom: "0.5rem", padding: "0.5rem 0.75rem", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "0.375rem", fontSize: "0.8rem", color: "#92400E" }}>
                                    <strong>Near-miss reason:</strong> {r.mismatch_reason}
                                  </div>
                                )}
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", fontSize: "0.8rem" }}>
                                  <div>
                                    <p style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--sage)", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 0.375rem" }}>File A — Full Record</p>
                                    {Object.entries(r.row_a || {}).map(([k, v]) => (
                                      <div key={k} style={{ display: "flex", borderBottom: "1px solid rgba(226,225,217,0.6)", padding: "0.2rem 0", fontSize: "0.78rem" }}>
                                        <span style={{ color: "var(--text-muted)", minWidth: "130px", flexShrink: 0 }}>{k}</span>
                                        <span style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                          <TransformCell original={v} transformed={r.row_a_transformed?.[k]} />
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                  <div>
                                    <p style={{ fontSize: "0.7rem", fontWeight: 700, color: "#1E40AF", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 0.375rem" }}>File B — Full Record</p>
                                    {Object.entries(r.row_b || {}).filter(([k]) => !k.startsWith("_AGG")).map(([k, v]) => (
                                      <div key={k} style={{ display: "flex", borderBottom: "1px solid rgba(226,225,217,0.6)", padding: "0.2rem 0", fontSize: "0.78rem" }}>
                                        <span style={{ color: "var(--text-muted)", minWidth: "130px", flexShrink: 0 }}>{k}</span>
                                        <span style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                          <TransformCell original={v} transformed={r.row_b_transformed?.[k]} />
                                        </span>
                                      </div>
                                    ))}
                                    {r.match_type === "matched_aggregate" && r.agg_group && (
                                      <div style={{ marginTop: "0.75rem" }}>
                                        <p style={{ fontSize: "0.7rem", fontWeight: 700, color: "#5B21B6", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 0.375rem" }}>Aggregate Group ({r.agg_group.length} rows)</p>
                                        {r.agg_group.slice(0, 10).map((ag, gi) => (
                                          <div key={gi} style={{ border: "1px solid #DDD6FE", borderRadius: "0.375rem", padding: "0.375rem 0.5rem", margin: "0.25rem 0", fontSize: "0.72rem", fontFamily: "JetBrains Mono, monospace", background: "white" }}>
                                            {Object.entries(ag).slice(0, 6).map(([k, v]) => (
                                              <span key={k} style={{ marginRight: "0.75rem" }}>
                                                <span style={{ color: "var(--text-muted)" }}>{k}:</span> {v}
                                              </span>
                                            ))}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            </>
          )}

          {/* Footer */}
          {!matchMode && activeTab !== "manual" && !loading && total > 0 && (
            <div style={{ padding: "0.75rem 1rem", borderTop: "1px solid var(--border-default)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: 0 }}>
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total} records
              </p>
              {totalPages > 1 && (
                <div style={{ display: "flex", gap: "0.375rem" }}>
                  <button className="btn-secondary" onClick={() => loadPage(page - 1)} disabled={page === 0}
                    style={{ padding: "0.25rem 0.75rem", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "0.25rem", opacity: page === 0 ? 0.4 : 1 }}>
                    <ChevronLeft size={12} /> Prev
                  </button>
                  <button className="btn-secondary" onClick={() => loadPage(page + 1)} disabled={page >= totalPages - 1}
                    style={{ padding: "0.25rem 0.75rem", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "0.25rem", opacity: page >= totalPages - 1 ? 0.4 : 1 }}>
                    Next <ChevronRight size={12} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Floating action bar in match mode. */}
      {matchMode && (
        <div data-testid="manual-match-action-bar"
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0,
            background: "white", borderTop: "1px solid var(--sage-border)",
            padding: "0.75rem 1.5rem", zIndex: 45,
            boxShadow: "0 -8px 24px rgba(92,122,94,0.10)",
            display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
          }}>
          <div style={{
            fontSize: "0.8rem", fontWeight: 600,
            padding: "0.35rem 0.75rem", borderRadius: "9999px",
            background: sel.state === "ok" ? "#D1FAE5" : sel.state === "invalid" ? "#FEE2E2" : "#F3F4F6",
            color: sel.state === "ok" ? "#065F46" : sel.state === "invalid" ? "#991B1B" : "#4B5563",
          }}>
            <Sparkles size={12} style={{ display: "inline", marginRight: "0.3rem", verticalAlign: "middle" }} />
            {sel.msg}
          </div>

          <select data-testid="manual-reason" className="recon-input"
            value={manualReason} onChange={e => setManualReason(e.target.value)}
            style={{ fontSize: "0.82rem", minWidth: "220px" }}>
            {MANUAL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>

          <input data-testid="manual-remarks" type="text" className="recon-input"
            value={manualRemarks} onChange={e => setManualRemarks(e.target.value)}
            placeholder={manualReason === "other" ? "Explain (required for Other)" : "Remarks (optional)"}
            style={{ flex: 1, minWidth: "200px", fontSize: "0.82rem" }} />

          <button data-testid="manual-match-submit"
            className="btn-primary"
            disabled={sel.state !== "ok" || submittingMatch || (manualReason === "other" && !manualRemarks.trim())}
            onClick={submitManualMatch}
            style={{
              opacity: (sel.state !== "ok" || submittingMatch || (manualReason === "other" && !manualRemarks.trim())) ? 0.5 : 1,
              fontSize: "0.85rem", padding: "0.45rem 1.1rem",
              display: "flex", alignItems: "center", gap: "0.375rem",
            }}>
            <Link2 size={14} /> {submittingMatch ? "Matching…" : "Match selected"}
          </button>

          <button onClick={() => { setSelectedA(new Set()); setSelectedB(new Set()); setManualRemarks(""); setMatchError(""); }}
            className="btn-secondary"
            style={{ fontSize: "0.82rem", padding: "0.4rem 0.9rem" }}>
            Clear
          </button>

          {matchError && (
            <div data-testid="manual-match-error"
              style={{ flexBasis: "100%", fontSize: "0.78rem", color: "#991B1B",
                background: "#FEE2E2", border: "1px solid #FCA5A5",
                borderRadius: "0.375rem", padding: "0.4rem 0.6rem", marginTop: "0.25rem" }}>
              {matchError}
            </div>
          )}
        </div>
      )}

      {/* Floating hover tooltip for matched rows */}
      {hoveredRow !== null && activeTab === "matched" && results[hoveredRow]?.row_b && (
        <div data-testid="hover-tooltip"
          style={{
            position: "fixed",
            left: Math.min(tooltipPos.x + 18, window.innerWidth - 400),
            top: Math.min(tooltipPos.y + 12, window.innerHeight - 340),
            background: "white", border: "1px solid var(--sage-border)",
            borderRadius: "0.75rem", padding: "0.875rem 1rem",
            maxWidth: "380px", maxHeight: "320px", overflowY: "auto",
            boxShadow: "0 10px 40px rgba(92,122,94,0.18)",
            zIndex: 1000, pointerEvents: "none",
            fontSize: "0.78rem"
          }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#1E40AF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.5rem", paddingBottom: "0.375rem", borderBottom: "1px solid var(--border-default)" }}>
            {results[hoveredRow].match_type === "matched_aggregate" ? "Aggregate Match → File B" : "Matched File B Record"}
          </div>
          {Object.entries(results[hoveredRow].row_b || {}).filter(([k]) => !k.startsWith("_AGG")).slice(0, 12).map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: "0.5rem", padding: "0.175rem 0", borderBottom: "1px solid #F0F0EE" }}>
              <span style={{ color: "var(--text-muted)", minWidth: "100px", flexShrink: 0, fontSize: "0.72rem" }}>{k}</span>
              <span style={{ fontFamily: "JetBrains Mono, monospace", wordBreak: "break-all" }}>{v ?? "—"}</span>
            </div>
          ))}
          {results[hoveredRow].match_type === "matched_aggregate" && results[hoveredRow].agg_group && (
            <div style={{ marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border-default)" }}>
              <span style={{ fontSize: "0.7rem", color: "#5B21B6", fontWeight: 600 }}>
                Agg group: {results[hoveredRow].agg_group.length} rows · Σ = {results[hoveredRow].agg_value?.toFixed?.(2) ?? "—"}
              </span>
            </div>
          )}
          <div style={{ marginTop: "0.5rem", paddingTop: "0.375rem", borderTop: "1px solid var(--border-default)", fontSize: "0.68rem", color: "var(--text-muted)", fontStyle: "italic" }}>
            Click row to expand · Matched by "{results[hoveredRow].rule_name}"
          </div>
        </div>
      )}

      {/* Flag modal */}
      {flagRow && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setFlagRow(null)} />
          <div data-testid="flag-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "440px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Flag size={16} style={{ color: "#DC2626" }} /> Flag as Exception
              </h3>
              <button data-testid="flag-close" onClick={() => setFlagRow(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
            </div>
            <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", marginBottom: "0.75rem", lineHeight: 1.5 }}>
              Record this row as an exception for follow-up. Add notes to explain the issue.
            </p>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Status</label>
              <select data-testid="flag-status" className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }}
                value={flagStatus} onChange={e => setFlagStatus(e.target.value)}>
                <option value="review">Review</option>
                <option value="in_progress">In Progress</option>
                <option value="escalated">Escalated</option>
                <option value="ignored">Ignored</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Assign to (optional)</label>
              <select data-testid="flag-assignee" className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }}
                value={flagAssignee} onChange={e => setFlagAssignee(e.target.value)}>
                <option value="">— unassigned —</option>
                {deptUsers.map(u => <option key={u.id} value={u.email}>{u.email}{u.full_name ? ` (${u.full_name})` : ""}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Notes</label>
              <textarea data-testid="flag-notes" className="recon-input" style={{ width: "100%", fontSize: "0.85rem", minHeight: "90px", fontFamily: "inherit" }}
                value={flagNotes} onChange={e => setFlagNotes(e.target.value)}
                placeholder="What's wrong? Next steps?" />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button data-testid="flag-cancel" onClick={() => setFlagRow(null)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="flag-submit" onClick={submitFlag} className="btn-primary" style={{ fontSize: "0.85rem" }}>Flag</button>
            </div>
          </div>
        </div>
      )}

      {/* E7 Ignore floating bar (unmatched_a / unmatched_b tabs) */}
      {(activeTab === "unmatched_a" || activeTab === "unmatched_b") && selectedIgnore.size > 0 && !ignoreModalOpen && (
        <div data-testid="ignore-action-bar"
          style={{
            position: "fixed", bottom: 0, left: 240, right: 0,
            background: "white", borderTop: "1px solid #D1D5DB",
            padding: "0.75rem 1.5rem", zIndex: 45,
            boxShadow: "0 -8px 24px rgba(0,0,0,0.08)",
            display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
          }}>
          <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
            {selectedIgnore.size} row{selectedIgnore.size === 1 ? "" : "s"} selected
          </span>
          <button data-testid="ignore-open-btn"
            onClick={() => { setIgnoreReason("non_contributing"); setIgnoreRemarks(""); setIgnoreModalOpen(true); }}
            className="btn-primary"
            style={{ fontSize: "0.82rem", padding: "0.4rem 0.85rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            Mark as Ignored / Non-contributing
          </button>
          <button onClick={() => setSelectedIgnore(new Set())}
            className="btn-secondary"
            style={{ fontSize: "0.82rem", padding: "0.4rem 0.85rem" }}>
            Clear
          </button>
        </div>
      )}

      {/* E7 Ignore confirmation modal */}
      {ignoreModalOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setIgnoreModalOpen(false)} />
          <div data-testid="ignore-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "440px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.9rem" }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0 }}>
                Mark as Ignored
              </h3>
              <button onClick={() => setIgnoreModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
            </div>
            <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", margin: "0 0 0.85rem", lineHeight: 1.5 }}>
              {selectedIgnore.size} row{selectedIgnore.size === 1 ? "" : "s"} will be moved to the <strong>Ignored</strong> tab. They'll count as <em>resolved</em> (not outstanding) in reports, but stay fully audit-traceable.
            </p>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Reason</label>
            <select data-testid="ignore-reason" className="recon-input" value={ignoreReason} onChange={e => setIgnoreReason(e.target.value)} style={{ width: "100%", marginBottom: "0.75rem", fontSize: "0.85rem" }}>
              {IGNORE_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Remarks (optional)</label>
            <textarea data-testid="ignore-remarks" className="recon-input"
              value={ignoreRemarks} onChange={e => setIgnoreRemarks(e.target.value)}
              placeholder="Why are these non-contributing?"
              style={{ width: "100%", fontSize: "0.85rem", minHeight: "80px", fontFamily: "inherit", marginBottom: "1rem" }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button data-testid="ignore-cancel" onClick={() => setIgnoreModalOpen(false)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="ignore-submit" onClick={submitIgnore} disabled={ignoring} className="btn-primary" style={{ fontSize: "0.85rem" }}>
                {ignoring ? "Marking…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* #2 Partial approve/reject floating bar */}
      {activeTab === "partial" && selectedPartial.size > 0 && !partialDecisionOpen && (        <div data-testid="partial-action-bar"
          style={{
            position: "fixed", bottom: 0, left: 240, right: 0,
            background: "white", borderTop: "1px solid var(--sage-border)",
            padding: "0.75rem 1.5rem", zIndex: 45,
            boxShadow: "0 -8px 24px rgba(92,122,94,0.10)",
            display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
          }}>
          <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
            {selectedPartial.size} partial row{selectedPartial.size === 1 ? "" : "s"} selected
          </span>
          <button data-testid="partial-approve-btn"
            onClick={() => { setPartialDecisionOpen("approve"); setPartialRemarks(""); }}
            className="btn-primary"
            style={{ fontSize: "0.82rem", padding: "0.4rem 0.85rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <Check size={13} /> Approve → Manual
          </button>
          <button data-testid="partial-reject-btn"
            onClick={() => { setPartialDecisionOpen("reject"); setPartialRemarks(""); }}
            className="btn-secondary"
            style={{ fontSize: "0.82rem", padding: "0.4rem 0.85rem", display: "flex", alignItems: "center", gap: "0.3rem", color: "#991B1B", borderColor: "#FCA5A5" }}>
            <X size={13} /> Reject → Unmatched
          </button>
          <button onClick={() => setSelectedPartial(new Set())}
            className="btn-secondary"
            style={{ fontSize: "0.82rem", padding: "0.4rem 0.85rem" }}>
            Clear
          </button>
        </div>
      )}

      {/* #2 Partial decision confirmation modal */}
      {partialDecisionOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setPartialDecisionOpen(null)} />
          <div data-testid="partial-decision-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "440px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0 }}>
                {partialDecisionOpen === "approve" ? "Approve partial matches" : "Reject partial matches"}
              </h3>
              <button onClick={() => setPartialDecisionOpen(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
            </div>
            <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", marginBottom: "0.75rem", lineHeight: 1.5 }}>
              {partialDecisionOpen === "approve"
                ? `${selectedPartial.size} partial row(s) will be promoted to Manual matches.`
                : `${selectedPartial.size} partial row(s) will be split into Unmatched A and Unmatched B records.`}
            </p>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Remarks (optional)</label>
            <textarea data-testid="partial-remarks" className="recon-input"
              value={partialRemarks} onChange={e => setPartialRemarks(e.target.value)}
              placeholder={partialDecisionOpen === "approve" ? "Why are these OK?" : "Why are these not a match?"}
              style={{ width: "100%", fontSize: "0.85rem", minHeight: "80px", fontFamily: "inherit", marginBottom: "1rem" }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => setPartialDecisionOpen(null)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="partial-decision-submit" onClick={submitPartialDecision} className="btn-primary" style={{ fontSize: "0.85rem" }}>
                {partialDecisionOpen === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HTML export prompt */}
      {htmlPromptOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setHtmlPromptOpen(false)} />
          <div data-testid="html-export-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "440px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: "0 0 0.5rem" }}>Export HTML report</h3>
            <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", marginBottom: "1rem" }}>
              Include the Partial / Review tab? Useful when sending for approval.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => setHtmlPromptOpen(false)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="html-export-no-partial" onClick={() => doHtmlReport(false)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Without partial</button>
              <button data-testid="html-export-with-partial" onClick={() => doHtmlReport(true)} className="btn-primary" style={{ fontSize: "0.85rem" }}>Include partial</button>
            </div>
          </div>
        </div>
      )}

      {/* Column selector modal */}
      {colsOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setColsOpen(false)} />
          <div data-testid="column-selector-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "640px", maxHeight: "85vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Columns3 size={16} /> Display columns ({activeTab})
              </h3>
              <button onClick={() => setColsOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
            </div>
            <p style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: "0.75rem", lineHeight: 1.5 }}>
              Pick columns to show in this table. Defaults to first 5 per file. Expanded row details always show every column. Saved per-run in your browser.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
              <button data-testid="col-sel-defaults"
                onClick={() => {
                  setColPrefs(p => ({ ...p, [activeTab]: { a: availA.slice(0, DEFAULT_COL_LIMIT), b: availB.slice(0, DEFAULT_COL_LIMIT) } }));
                }}
                className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.65rem" }}>Reset to defaults (5+5)</button>
              <button data-testid="col-sel-all"
                onClick={() => setColPrefs(p => ({ ...p, [activeTab]: { a: [...availA], b: [...availB] } }))}
                className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.65rem" }}>Select all</button>
              <button data-testid="col-sel-none"
                onClick={() => setColPrefs(p => ({ ...p, [activeTab]: { a: [], b: [] } }))}
                className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.65rem" }}>Clear</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              {[
                { side: "A", all: availA, color: "var(--sage)", bg: "#F0F7F0" },
                { side: "B", all: availB, color: "#1E40AF", bg: "#EEF4FE" },
              ].map(({ side, all, color, bg }) => {
                const selected = side === "A" ? effectiveA : effectiveB;
                return (
                  <div key={side} style={{ border: "1px solid var(--border-default)", borderRadius: "0.5rem", padding: "0.625rem", background: bg }}>
                    <div style={{ fontSize: "0.78rem", fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>
                      File {side} ({selected.length}/{all.length})
                    </div>
                    <div style={{ maxHeight: "45vh", overflowY: "auto" }}>
                      {all.length === 0 ? (
                        <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", padding: "0.5rem" }}>No columns</div>
                      ) : all.map(k => {
                        const checked = selected.includes(k);
                        return (
                          <label key={k} data-testid={`col-opt-${side.toLowerCase()}-${k}`}
                            style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.25rem 0.3rem", cursor: "pointer", fontSize: "0.8rem", fontFamily: "JetBrains Mono, monospace" }}>
                            <input type="checkbox" checked={checked}
                              onChange={() => {
                                setColPrefs(p => {
                                  const cur = p[activeTab] || { a: [...effectiveA], b: [...effectiveB] };
                                  const key = side === "A" ? "a" : "b";
                                  const list = new Set(cur[key] || []);
                                  if (checked) list.delete(k); else list.add(k);
                                  // Preserve ordering: filter availSide by membership
                                  const ordered = (side === "A" ? availA : availB).filter(c => list.has(c));
                                  return { ...p, [activeTab]: { ...cur, [key]: ordered } };
                                });
                              }}
                              style={{ accentColor: color }} />
                            {k}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1rem" }}>
              <button data-testid="col-sel-save-team"
                onClick={saveTeamPrefs}
                disabled={savingPrefs || !run?.rule_set_id}
                className="btn-secondary"
                style={{ fontSize: "0.78rem", padding: "0.35rem 0.7rem", display: "flex", alignItems: "center", gap: "0.3rem", opacity: savingPrefs ? 0.5 : 1 }}>
                <Save size={12} /> {savingPrefs ? "Saving…" : "Save as team default"}
              </button>
              <button data-testid="col-sel-done" onClick={() => setColsOpen(false)} className="btn-primary" style={{ fontSize: "0.85rem" }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
